import { useEffect, useState } from "react";
import { useMemo } from "react";
import { TrendingUp, AlertCircle, ArrowUpRight, X } from "react-feather";
import { Badge, Offcanvas } from "react-bootstrap";
import CustomerInsightCard from "./CustomerInsightCard";
import SendNowBar from "./SendNowBar";
import api from "../api";
import { PaystackButton } from "react-paystack";
import SMSBulkPopup from "./SMSBulkPopup";

const InsightsDashboard = ({ insights, website }) => {

  const [showPopup, setShowPopup] = useState(false);
  const [popupTasks, setPopupTasks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sortBy, setSortBy] = useState("days_to_reorder");
  const [sortOrder, setSortOrder] = useState("asc");
  const [vendorSMS, setVendorSMS] = useState(0);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(200);

  const [searchQuery, setSearchQuery] = useState("");

  const [panel, setPanel] = useState({
    show: false,
    type: null,
  });

  const getCustomerId = (c) =>
    `${c.customer_name}-${c.action?.cust_phone}`;

  const handleOpen = (type) => {
    resetPanelState(); // 🔥 reset BEFORE opening
    setPanel({ show: true, type });
  };

  const handleClose = () => {
    setPanel({ show: false, type: null });
    resetPanelState(); // 🔥 reset AFTER closing
  };

const handleBulkSend = () => {
  if (selected.length === 0) return;

  // 🚨 BLOCK if insufficient SMS
  if (smsNeeded > vendorSMS) {
    setShowRecharge(true);
    return;
  }

  const tasks = selected
    .map((item) => {
      const c = customerMap.get(item.id);

      if (!c) return null;

      return {
        id: item.id,
        customer: c.customer_name,
        cust_phone: c.action?.cust_phone,
        action: c.action, // ✅ IMPORTANT (popup uses this)
        potential_value: c.potential_value,
      };
    })
    .filter(Boolean);

  setPopupTasks(tasks);
  setShowPopup(true);
};

  const {
    revenue_opportunity,
    customer_states,
    ai_summary,
    at_risk_customers_list,
    opportunity_customers,
    timing_insights,
  } = insights;

  const reorder = revenue_opportunity?.ready_to_reorder_value || 0;
  const refill = revenue_opportunity?.refill_value || 0;
  const atRisk = revenue_opportunity?.at_risk_revenue || 0;

  const total = reorder + refill + atRisk;
  const safeTotal = total > 0 ? total : 1;

  const percent = (val) => ((val / safeTotal) * 100).toFixed(1);

  const getPanelContent = () => {
    switch (panel.type) {
      case "opportunity":
        return {
          title: "Revenue Opportunity",
          customers: Array.isArray(insights?.opportunity_customers)
            ? insights.opportunity_customers
            : [],
        };

      case "atRisk":
        return {
          title: "At Risk Customers",
          customers: Array.isArray(insights?.at_risk_customers_list)
            ? insights.at_risk_customers_list
            : [],
        };

      default:
        return { title: "", customers: [] };
    }
  };

  const { title, customers } = getPanelContent();

  const toggleSelectAll = () => {
    if (isAllSelected) {
      clearSelection();
    } else {
      const allSelected = sortedCustomers
        .filter((c) => c && c.action)
        .map((c) => ({
          id: getCustomerId(c),
          potential_value: c.potential_value || 0,
        }));

      setSelected(allSelected);
    }
  };
  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected]
  );

  const isSelected = (id) => selectedIds.has(id);

  const clearSelection = () => setSelected([]);
  const sortedCustomers = [...customers].sort((a, b) => {
  const getValue = (c) => {
    switch (sortBy) {
      case "potential_value":
        return (
          c.potential_value?.parsedValue ??
          parseFloat(c.potential_value) ??
          0
        );

      case "last_order_days":
        return Number(c.last_order_days) || 0;

      case "days_to_reorder":
      default:
        return Number(c.days_to_reorder) || 0;
    }
  };

  const valA = getValue(a);
  const valB = getValue(b);

    return sortOrder === "asc" ? valA - valB : valB - valA;
  });
  // ✅ FIX: use stable IDs
  const allIds = useMemo(() => {
    return sortedCustomers
      .filter((c) => c && c.action)
      .map((c) => getCustomerId(c));
  }, [sortedCustomers]);

  const isAllSelected =
    allIds.length > 0 &&
    allIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id, potential_value) => {
    setSelected((prev) => {
      const exists = prev.find((item) => item.id === id);

      if (exists) {
        return prev.filter((item) => item.id !== id);
      } else {
        return [...prev, { id, potential_value }];
      }
    });
  };

  const totalPotential = Number(
      selected.reduce(
        (sum, item) => sum + (item.potential_value || 0),
        0
      ).toFixed(2)
  );

  const customerMap = useMemo(() => {
  const map = new Map();

  sortedCustomers.forEach((c) => {
    if (!c || !c.action) return;

    const id = getCustomerId(c);
    map.set(id, c);
  });

  return map;
}, [sortedCustomers]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    api
      .get(baseUrl + "/vendor-profile/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      .then((res) => {
        setVendorSMS(res.data.sms_balance || 0);
      })
      .catch(() => {});
  }, []);

  const vendorDataRaw = localStorage.getItem("VendorData");
    let vendorEmail = "demo@kadhr.com";

    try {
      if (vendorDataRaw) {
        const vendorData = JSON.parse(vendorDataRaw);
        if (vendorData?.email) {
          vendorEmail = vendorData.email;
        }
      }
    } catch {}

    const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

    const paystackProps = {
      email: vendorEmail,
      amount: rechargeAmount * 100,
      currency: "KES",
      metadata: { purpose: "SMS Recharge" },
      publicKey,
      text: "Confirm Recharge",

      onSuccess: async (reference) => {
        try {
          await api.post(
            process.env.REACT_APP_BASE_URL + "/sms-checkout/",
            {
              total_amount: rechargeAmount,
              email: vendorEmail,
              trxref: reference?.reference,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
            }
          );

          setVendorSMS((prev) => prev + rechargeAmount);
          setShowRecharge(false);
        } catch {}
      },

      onClose: () => setShowRecharge(false),
    };

      const smsNeeded = selected.length;
      const insufficientSMS = smsNeeded > vendorSMS;

      // 🔥 NEW: FILTER LOGIC (add AFTER sortedCustomers)
      const filteredCustomers = useMemo(() => {
        if (!searchQuery.trim()) return sortedCustomers;

        const q = searchQuery.toLowerCase();

        return sortedCustomers.filter((c) => {
          const name = c.customer_name?.toLowerCase() || "";
          const phone = c.action?.cust_phone || "";
          const message = c.action?.message?.toLowerCase() || "";

          return (
            name.includes(q) ||
            phone.includes(q) ||
            message.includes(q)
          );
        });
      }, [sortedCustomers, searchQuery]);
      const resetPanelState = () => {
        setSelected([]);
        setSearchQuery("");
      };

    const getInsightMeta = (type) => {
      switch (type) {
        case "opportunity":
          return {
            description:
              "Customers ready or close to reordering — act now to capture immediate revenue.",
            icon: <TrendingUp size={16} />,
            iconBg: "#e6fcf5",
            iconColor: "#00a67e",
          };

        case "atRisk":
          return {
            description:
              "Customers slipping away — re-engage them early to recover lost revenue.",
            icon: <AlertCircle size={16} />,
            iconBg: "#fff5f5",
            iconColor: "#fa5252",
          };

        default:
          return {
            description:
              "Customers grouped by behavior — take action to drive repeat revenue.",
            icon: <TrendingUp size={16} />,
            iconBg: "#f1f3f5",
            iconColor: "#495057",
          };
      }
    };
    const meta = getInsightMeta(panel.type);
  return (
    <>
      <div className="row">

        <div className="col-12">
          <div className="stat-tile bg-white shadow-sm">
            <div className="w-100">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 font-bold font1">Revenue Forecast </h6>
                <Badge className="bg-primary-subtle text-primary br5 p-1 ms-1 font8">by Customer Behavior</Badge>
              </div>

              <h4 className="mb-3 font-bold">KES {total.toLocaleString()}</h4>

              <div
                className="d-flex w-100"
                style={{ height: "10px", borderRadius: "8px", overflow: "hidden" }}
              >
                {refill > 0 && (
                  <div style={{ width: `${percent(refill)}%`, background: "#00cb93" }} />
                )}
                {reorder > 0 && (
                  <div style={{ width: `${percent(reorder)}%`, background: "#f5c542" }} />
                )}
                {atRisk > 0 && (
                  <div style={{ width: `${percent(atRisk)}%`, background: "#ff0e2e" }} />
                )}
              </div>

              <div className="mt-3 d-flex flex-wrap gap-3">
                {refill > 0 && <small><span className="dott bglgreen"></span> KES {refill.toLocaleString()}</small>}
                {reorder > 0 && <small><span className="dott bglorange"></span> KES {reorder.toLocaleString()}</small>}
                {atRisk > 0 && <small><span className="dott bgreg"></span> KES {atRisk.toLocaleString()}</small>}
              </div>

              <small className="text-muted mt-3 d-block">
                {(() => {
                  const total = atRisk + refill + reorder;

                  if (total === 0) {
                    return "No revenue data yet — start capturing sales to unlock insights.";
                  }

                  const atRiskPct = (atRisk / total) * 100;
                  const refillPct = (refill / total) * 100;
                  const reorderPct = (reorder / total) * 100;

                  // 🔥 HIGH RISK ALERT
                  if (atRiskPct >= 40) {
                    return `⚠️ ${atRiskPct.toFixed(0)}% of your revenue is at risk — check recommended tasks below.`;
                  }

                  // ⚠️ MODERATE RISK
                  if (atRiskPct >= 25) {
                    return `⚠️ ${atRiskPct.toFixed(0)}% of your revenue is slipping — review recommended tasks below.`;
                  }

                  // 🔁 STRONG REORDER SIGNAL
                  if (reorderPct >= 40) {
                    return `🔥 ${reorderPct.toFixed(0)}% of your revenue is ready to reorder — see recommended tasks below.`;
                  }

                  // 💧 STRONG REFILL SIGNAL
                  if (refillPct >= 40) {
                    return `💧 ${refillPct.toFixed(0)}% of your revenue is active — check recommended tasks below.`;
                  }

                  // ⚖️ BALANCED STATE
                  if (
                    Math.abs(atRiskPct - refillPct) < 10 &&
                    Math.abs(refillPct - reorderPct) < 10
                  ) {
                    return "⚖️ Your revenue is well balanced — explore recommended tasks below to keep it growing.";
                  }

                  // 📈 GENERAL GUIDANCE (fallback)
                  if (reorderPct > refillPct && reorderPct > atRiskPct) {
                    return `🔁 ${reorderPct.toFixed(0)}% of your revenue is ready — see recommended tasks below.`;
                  }

                  if (refillPct > reorderPct && refillPct > atRiskPct) {
                    return `💧 ${refillPct.toFixed(0)}% of your revenue is growing — check recommended tasks below.`;
                  }

                  return `⚠️ ${atRiskPct.toFixed(0)}% of your revenue needs attention — review recommended tasks below.`;
                })()}
              </small>

              <hr />
              <div className="mt-3 d-flex flex-wrap gap-3 font9">
                <small><span className="dott-mini bglgreen"></span> Ready to order</small>
                <small><span className="dott-mini bglorange"></span> Upcoming</small>
                <small><span className="dott-mini bgreg"></span> At Risk</small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 mt-4 mb-3">
          <div className="row g-2">

            <div className="col-6">
              <div className="stat-tile bg-white shadow-sm"
                onClick={(e) => { e.stopPropagation(); handleOpen("opportunity"); }}
                style={{ cursor: "pointer" }}>
                <div>
                  <TrendingUp size={18} className="mb-2 text-success" />
                  <h6 className="mb-0 font-bold">                    
                    <span className="dott-mini bglgreen"></span>
                    <span className="dott-mini bglorange me-1"></span>Active</h6>
                  <div>
                     <small className="text-muted">
                       {(opportunity_customers?.length || 0).toLocaleString()} customers
                    </small>
                  </div>
                  <div className="mt-1">
                    <small>KES {(refill + reorder).toLocaleString()}</small>
                  </div>
                </div>
                <div className="link-side-rtg"><ArrowUpRight /></div>
              </div>
            </div>

            <div className="col-6">
              <div className="stat-tile bg-white shadow-sm"
                onClick={(e) => { e.stopPropagation(); handleOpen("atRisk"); }}
                style={{ cursor: "pointer" }}>
                <div>
                  <AlertCircle size={18} className="mb-2 text-danger" />
                  <h6 className="mb-0 font-bold"><span className="dott-mini bgreg me-1"></span> At Risk</h6>
                  <small className="text-muted">
                    {(at_risk_customers_list?.length || 0).toLocaleString()} customers
                  </small>
                  <div className="mt-1">
                    <small>KES {(atRisk).toLocaleString()}</small>
                  </div>
                </div>
                <div className="link-side-rtg"><ArrowUpRight /></div>
              </div>
            </div>

          </div>
        </div>

        <Offcanvas show={panel.show} onHide={handleClose} placement="end" className="bg-lblue0">
          <Offcanvas.Header closeButton  className="bg-lblue0 pb-2 pt-2">
            <div className="d-flex justify-content-between align-items-start gap-1">
                  <div className="px-2 py-1 rounded-3 bg-white border-0 shadow-tile small">
                    <span className="text-muted">SMS Credits:</span>{" "}
                    <span className={`fw-semibold ${insufficientSMS ? "text-danger" : ""}`}>
                      {vendorSMS}
                    </span>

                    {insufficientSMS && (
                      <span
                        className="badge bg-danger ms-2 pointer"
                        onClick={() => setShowRecharge(true)}
                      >
                        Recharge
                      </span>
                    )}
                  </div>
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <div className="pb-2">

              {/* LEFT: Title + Context */}
              <div className="bg-white p-3 rounded-4">
                <div className="d-flex align-items-center gap-2 mb-1">
                  
                  {/* ICON */}
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "8px",
                      background: meta.iconBg,
                      color: meta.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {meta.icon}
                  </div>

                  {/* TITLE */}
                  <h5 className="fw-semibold mb-0">
                    {title}
                  </h5>
                </div>

                {/* DESCRIPTION */}
                <div className="text-muted small">
                  {meta.description}
                </div>
              </div>

              {/* SECOND ROW */}
              {/* 🔥 UPDATED HEADER (added search input BELOW controls) */}
            <div className="sticky-select-header">
              <div className="d-flex justify-content-between align-items-center pt-2 bg-lblue0">

                <div className="d-flex align-items-center gap-2 bg-lblue0 pb-3 pt-3 pr-3">
                  <input
                    type="checkbox"
                    className="form-check-input custom-checkbox-lg"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                  <small className="text-muted pl-2 pr-2 pt-1 lh0">
                    {selected.length > 0
                      ? `Send all (${selected.length} selected)`
                      : "Send all"}
                  </small>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select form-select-sm w-auto shadow-tile border-0 w100-mini-mbl"
                    value={sortBy}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSortBy(value);
                      setSortOrder(value === "potential_value" ? "desc" : "asc");
                    }}
                  >
                    <option value="days_to_reorder">Reorder urgency</option>
                    <option value="potential_value">Potential order value</option>
                    <option value="last_order_days">Last order</option>
                  </select>

                  <button
                    className="btn btn-sm btn-dark"
                    onClick={() =>
                      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                    }
                  >
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </button>
                </div>
              </div>

              {/* 🔍 NEW SEARCH BAR */}
              <div className="px-1 pb-2 bg-lblue0">
                <input
                  type="text"
                  className="form-control form-control-sm shadow-tile border-0"
                  placeholder="Search customer, phone, or message..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            </div>
            {!Array.isArray(customers) || customers.length === 0 ? (
              <div className="text-center p-4 bg-inherit rounded-4">

                <p className="mb-1 fw-semibold">
                  This segment doesn’t have customers yet
                </p>

                <small className="text-muted">
                  As customers start falling into this group, they’ll appear here with recommended actions.
                </small>

              </div>
            ) : (
              <>
                  {/* 🔥 CHANGE THIS ONLY: sortedCustomers → filteredCustomers */}
                  <div className="row g-2">
                    {Array.isArray(filteredCustomers) &&
                      filteredCustomers
                        .filter((c) => c && c.action)
                        .map((c) => {
                          const id = getCustomerId(c);

                          return (
                            <CustomerInsightCard
                              key={id}
                              data={{
                                customer_name: c.customer_name,
                                total_spent: c.total_spent?.parsedValue ?? c.total_spent,
                                last_order_days: c.last_order_days,
                                days_to_reorder: c.days_to_reorder?.parsedValue ?? c.days_to_reorder,
                                avg_interval: c.avg_interval?.parsedValue ?? c.avg_interval,
                                behavior_label: c.behavior_label,
                                action: c.action,
                                type: c.type,
                                potential_value: c.potential_value,
                              }}
                              selected={isSelected(id)}
                              onSelect={() =>
                                toggleSelect(id, c.potential_value || 0)
                              }
                            />
                          );
                        })}
                  </div>
                  <SendNowBar
                    selectedCustomers={selected}
                    total={totalPotential}
                    currency={"KES"}
                    onSend={handleBulkSend}
                  />
                  <SMSBulkPopup
                    show={showPopup}
                    onClose={() => setShowPopup(false)}
                    tasks={popupTasks}
                    smslink={""} // optional: your shop link
                    onSuccess={() => {
                      setShowPopup(false);
                      setSelected([]); // ✅ clear after send
                      setVendorSMS((prev) => prev - popupTasks.length); // ✅ deduct SMS
                    }}
                  />

                  {showRecharge && (
                    <div
                      className="modal fade show d-block"
                      style={{ background: "rgba(0,0,0,0.6)" }}
                    >
                      <div className="modal-dialog modal-m mt70">
                        <div className="modal-content">
                          <div className="modal-header pb-2 pt-2 border-0 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 font-bold">Recharge SMS</h6>

                            <button
                              onClick={() => setShowRecharge(false)}
                              className="btn btn-sm ms-auto"
                            >
                              <X size={18} />
                            </button>
                          </div>

                          <div className="modal-body pt-0">
                            <p className="font9 text-muted mb-2">
                              You need {smsNeeded} SMS Credits but only have {vendorSMS}.
                            </p>

                            <input
                              type="number"
                              className="form-control mb-3"
                              value={rechargeAmount}
                              min="50"
                              onChange={(e) => setRechargeAmount(Number(e.target.value))}
                            />

                            <PaystackButton
                              {...paystackProps}
                              className="btn btn-dark col-12"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </>
            )}
          </Offcanvas.Body>
        </Offcanvas>

      </div>
    </>
  );
};

export default InsightsDashboard;